/**
 * The read-only «My project» grid a confined roster person sees in
 * `MySchedule`: the published plan of their related project(s), day x person.
 *
 * Pure, because vitest runs in node and cannot mount the component. The wire
 * shape is `ProjectRoster` (`GET /api/roster/me/project`): `days` is
 * `{ name: { iso_date: code } }`, a cell may stack several codes joined by `+`,
 * and a day the server must not name (another project) reads `"~"`.
 */
import { stackOf } from './derive'
import { OUT_OF_SCOPE, type ProjectRoster } from './types'

export interface ProjectCell {
  /** The stacked codes in display order. Empty never occurs: a blank day is `null`. */
  codes: string[]
  /** The server masked this day -- it is on another project. NEVER blank:
   *  blank would read as "free". */
  elsewhere: boolean
}

interface ProjectGridRow {
  name: string
  role: string
  home: string
  /** One entry per `days[i]`; `null` is a day with nothing planned. */
  cells: (ProjectCell | null)[]
}

interface ProjectGrid {
  days: string[]
  rows: ProjectGridRow[]
  /** The codes actually shown, with their legend colour ('' when unknown). */
  legend: { code: string; label: string; colour: string }[]
}

/** Every ISO date from `from` to `to`, inclusive. Empty if `to` precedes `from`. */
export function isoDaysBetween(from: string, to: string): string[] {
  const out: string[] = []
  const end = Date.parse(`${to}T00:00:00Z`)
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= end; t += 86_400_000) {
    out.push(new Date(t).toISOString().slice(0, 10))
  }
  return out
}

export function buildProjectGrid(data: ProjectRoster): ProjectGrid {
  const days = isoDaysBetween(data.from, data.to)
  const legendByCode = new Map(data.codes.map((c) => [c.code, c] as const))
  const shown = new Set<string>()
  const rows = data.people.map((p) => {
    const byDay = data.days[p.name] ?? {}
    const cells = days.map((day): ProjectCell | null => {
      const raw = byDay[day]
      if (!raw) return null
      if (raw === OUT_OF_SCOPE) {
        shown.add(OUT_OF_SCOPE)
        return { codes: [OUT_OF_SCOPE], elsewhere: true }
      }
      const codes = stackOf(raw)
      if (codes.length === 0) return null
      codes.forEach((c) => shown.add(c))
      return { codes, elsewhere: false }
    })
    return { name: p.name, role: p.role, home: p.home, cells }
  })
  const legend = [...shown].map((code) => ({
    code,
    label: legendByCode.get(code)?.label || code,
    colour: legendByCode.get(code)?.colour ?? '',
  }))
  return { days, rows, legend }
}
